import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSignature, 
  faEraser,
  faUser,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import './Auth.css';

interface ConsentFormProps {
  onSubmit: (consentData: any) => void;
  loading: boolean;
  userEmail?: string;
  userName?: string;
}

export const ConsentForm: React.FC<ConsentFormProps> = ({ onSubmit, loading, userEmail, userName }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  const [editableEmail, setEditableEmail] = useState(userEmail || '');
  const [editableName, setEditableName] = useState(userName || '');
  
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setEditableEmail(userEmail || '');
    setEditableName(userName || '');
  }, [userEmail, userName]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      }
    }
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    setHasSignature(true);
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
        ctx.stroke();
      }
    }
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
      }
    }
  };

  const validateForm = () => {
    const newErrors: string[] = [];
    if (!hasSignature) newErrors.push('Podpis jest wymagany.');
    if (!editableName.trim()) newErrors.push('Imię i nazwisko są wymagane.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editableEmail)) newErrors.push('Nieprawidłowy email.');
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSubmit({
      full_name: editableName.trim(),
      email: editableEmail.trim(),
      signature_data: canvasRef.current?.toDataURL() || ''
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-split-layout">
        <div className="auth-branding">
          <div className="brand-section">
            <img src="/logo-radonek.png" alt="Radonek Logo" className="auth-logo" />
            <div className="brand-info">
              <h1>Radonek</h1>
              <p className="brand-subtitle">Zgoda na udział w badaniu</p>
            </div>
          </div>
          <div className="about-section">
            <h3>Prospektywne obserwacyjne badanie naukowe</h3>
            <div className="key-features">
              <div className="feature-highlight">
                <div className="feature-icon">🔬</div>
                <div className="feature-content">
                  <h4>Cel badania</h4>
                  <p>Ocena użyteczności wirtualnego asystenta AI w edukacji i wsparciu pacjentów onkologicznych.</p>
                </div>
              </div>
              <div className="feature-highlight">
                <div className="feature-icon">🛡️</div>
                <div className="feature-content">
                  <h4>Ochrona danych</h4>
                  <p>Twoje dane są w pełni zanonimizowane i chronione zgodnie z RODO.</p>
                </div>
              </div>
              <div className="feature-highlight">
                <div className="feature-icon">✅</div>
                <div className="feature-content">
                  <h4>Jak to działa?</h4>
                  <p>Zadaj pytanie naszemu asystenowi. Asystent wykorzysta wiedzę na temat twojej choroby i dolegliwości, którą otrzymał od Twojego lekarza. Dzięki temu otrzymasz odpowiedzi dotyczące dokładnie Twojego przypadku.</p>
                  <p>Na zadane pytania otrzymasz dwie odpowiedzi: jedną wykorzystującą wiedzę, którą przygotowaliśmy specjalnie dla naszych pacjentów (RAG) i drugą od strandardowego modelu sztucznej inteligencji (ChatGPT 4o). Wybierz, która będzie bardziej pomocna. Odpowiedzi omówisz ze swoim lekarzem na wizycie. Udział jest dobrowolny i możesz go zakończyć w dowolnym momencie bez konsekwencji.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="auth-form-wrapper">
          <div className="auth-card">
            <div className="auth-header">
              <div className="form-title">
                <h2>Formularz zgody</h2>
                <p className="auth-form-subtitle">Prosimy o weryfikację danych i złożenie podpisu.</p>
              </div>
            </div>
            
            <div className="auth-form">
              {errors.length > 0 && (
                <div className="error-message">
                  {errors.map((err, i) => <div key={i}>{err}</div>)}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="fullName"><FontAwesomeIcon icon={faUser} /> Imię i nazwisko</label>
                <input
                  id="fullName"
                  type="text"
                  value={editableName}
                  onChange={(e) => setEditableName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email"><FontAwesomeIcon icon={faEnvelope} /> Adres email</label>
                <input
                  id="email"
                  type="email"
                  value={editableEmail}
                  onChange={(e) => setEditableEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label><FontAwesomeIcon icon={faSignature} /> Podpis elektroniczny</label>
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  className="signature-canvas"
                  style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-base)', cursor: 'crosshair' }}
                />
                <button type="button" onClick={clearSignature} className="auth-link" style={{alignSelf: 'flex-end'}}>
                  <FontAwesomeIcon icon={faEraser} /> Wyczyść podpis
                </button>
              </div>

              <div className="form-group">
                <p style={{fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textAlign: 'center'}}>
                  Składając podpis, oświadczam, że zapoznałem/am się z informacjami o badaniu i wyrażam świadomą zgodę na udział oraz na przetwarzanie moich danych w celach badawczych.
                </p>
              </div>

              <button onClick={handleSubmit} disabled={loading || !hasSignature} className="auth-button">
                {loading ? 'Przetwarzanie...' : 'Wyrażam zgodę i kontynuuję'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};